import { useState } from 'react';
import { useLang } from '@/i18n/LanguageContext';

function CiteIcon() {
  return (
    <svg viewBox="0 0 16 16" fill="none">
      <path d="M2 4.5 8 9l6-4.5M2 4h12v8H2V4Z" stroke="currentColor" strokeWidth="1.2" />
    </svg>
  );
}

function WebIcon() {
  return (
    <svg viewBox="0 0 16 16" fill="none">
      <circle cx="8" cy="8" r="6.3" stroke="currentColor" strokeWidth="1.2" />
      <path
        d="M2 8h12M8 1.7c1.8 1.6 2.8 3.9 2.8 6.3S9.8 12.7 8 14.3C6.2 12.7 5.2 10.4 5.2 8S6.2 3.3 8 1.7Z"
        stroke="currentColor"
        strokeWidth="1.2"
      />
    </svg>
  );
}

export default function Features() {
  const { t } = useLang();
  const [briefTab, setBriefTab] = useState<'history' | 'web'>('history');

  return (
    <section className="section section--alt" id="features" data-screen-label="Three inline features">
      <div className="container">
        <div className="section-head reveal">
          <span className="eyebrow">{t('feat.eyebrow')}</span>
          <h2 className="h-section">
            {t('feat.h2.line1')}
            <br />
            {t('feat.h2.line2')}
          </h2>
          <p className="lead">{t('feat.lead')}</p>
        </div>

        <div className="feature-grid">
          {/* Card 1 — Auto-Brief */}
          <article className="feature reveal" data-d="1">
            <div className="fnum">01</div>
            <div className="kills">{t('feat.c1.kills')}</div>
            <h3 className="h-card">{t('feat.c1.title')}</h3>
            <p className="body">{t('feat.c1.body')}</p>
            <div className="demo mock">
              <div className="brief-tabs">
                <button
                  className={`brief-tab${briefTab === 'history' ? ' active' : ''}`}
                  onClick={() => setBriefTab('history')}
                >
                  <svg viewBox="0 0 16 16" fill="none">
                    <path
                      d="M8 4v4l2.5 1.5"
                      stroke="currentColor"
                      strokeWidth="1.3"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    <path
                      d="M2.6 6.5A6 6 0 1 1 2.5 9.5M2.6 6.5H5M2.6 6.5V4"
                      stroke="currentColor"
                      strokeWidth="1.3"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                  From history
                </button>
                <button
                  className={`brief-tab web${briefTab === 'web' ? ' active' : ''}`}
                  onClick={() => setBriefTab('web')}
                >
                  <WebIcon />
                  From web
                </button>
              </div>
              <div id="briefResult">
                {/* FROM HISTORY */}
                <div className={`brief-pane${briefTab === 'history' ? ' active' : ''}`}>
                  <div className="brief2 history">
                    <div className="b2-pad">
                      <div className="b2-top">
                        <span className="b2-badge">Account Brief</span>
                        <span className="b2-pill stalled">Stalled</span>
                      </div>
                      <div className="b2-name">Granola</div>
                      <div className="b2-meta">8 threads · 2 meetings · last touch 12d ago</div>
                      <div className="b2-div" />
                      <div className="b2-lbl">Stakeholders</div>
                      <div className="b2-chips">
                        <span className="sh-chip">
                          <span className="a" style={{ background: '#16a571' }}>
                            CP
                          </span>
                          Chris <span className="r">champion</span>
                        </span>
                        <span className="sh-chip">
                          <span className="a" style={{ background: '#f5a623' }}>
                            RT
                          </span>
                          Rachel
                        </span>
                        <span className="sh-chip">
                          <span className="a" style={{ background: '#7c5cf0' }}>
                            DL
                          </span>
                          Dana
                        </span>
                      </div>
                      <div className="b2-div" />
                      <div className="b2-lbl">Where it stands</div>
                      <div className="b2-fact">Budget confirmed, demo done — stalled on scope for 12 days.</div>
                      <div className="b2-cite">
                        <CiteIcon />
                        Thread · "Re: pricing" · Apr 18
                      </div>
                      <div className="b2-lbl" style={{ marginTop: 14 }}>
                        Watch
                      </div>
                      <div className="b2-fact">Chris went quiet after pushback on price.</div>
                      <div className="b2-cite">
                        <CiteIcon />
                        Last reply · May 20
                      </div>
                      <div className="b2-next">
                        <div className="nx-l">Next move</div>
                        <div className="nx-t">Send a lighter-scope proposal to restart the thread.</div>
                      </div>
                    </div>
                    <div className="b2-foot">
                      <span className="src2">
                        <svg viewBox="0 0 16 16" fill="none">
                          <path
                            d="M4 7V5.5a4 4 0 1 1 8 0V7m-9 0h10v7H3V7Z"
                            stroke="currentColor"
                            strokeWidth="1.2"
                            strokeLinejoin="round"
                          />
                        </svg>
                        8 threads · 2 mtgs · HubSpot
                      </span>
                      <span className="open">Open full brief →</span>
                    </div>
                  </div>
                </div>
                {/* FROM WEB */}
                <div className={`brief-pane${briefTab === 'web' ? ' active' : ''}`}>
                  <div className="brief2 web">
                    <div className="b2-pad">
                      <div className="b2-top">
                        <span className="b2-badge">Account Brief</span>
                        <span className="b2-pill new">New</span>
                      </div>
                      <div className="b2-name">Northwind Labs</div>
                      <div className="b2-meta">New lead · no thread yet</div>
                      <div className="b2-div" />
                      <div className="b2-lbl">Company</div>
                      <div className="b2-fact">Series B HR-tech, ~120 employees, San Francisco.</div>
                      <div className="b2-cite">
                        <WebIcon />
                        northwind.com · Crunchbase
                      </div>
                      <div className="b2-div" />
                      <div className="b2-lbl">Stakeholders</div>
                      <div className="b2-chips">
                        <span className="sh-chip">
                          <span className="a" style={{ background: '#1f6fd6' }}>
                            AT
                          </span>
                          Aisha <span className="r">VP People</span>
                        </span>
                        <span className="sh-chip">
                          <span className="a" style={{ background: '#7c5cf0' }}>
                            MR
                          </span>
                          Marco <span className="r">CFO</span>
                        </span>
                      </div>
                      <div className="b2-next" style={{ background: '#e8f1fe', borderLeftColor: '#3d8bf0' }}>
                        <div className="nx-l" style={{ color: '#1f6fd6' }}>
                          Suggested opener
                        </div>
                        <div className="nx-t">
                          Open with a short intro referencing their recent Series B raise.
                        </div>
                      </div>
                    </div>
                    <div className="b2-foot">
                      <span className="src2">
                        <WebIcon />
                        Crunchbase · LinkedIn
                      </span>
                      <span className="open" style={{ color: '#1f6fd6' }}>
                        Draft intro →
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="feat-foot">
              <span className="chip">
                <span className="dot" />
                {t('feat.c1.foot1')}
              </span>
              <span className="chip">{t('feat.c1.foot2')}</span>
            </div>
          </article>

          {/* Card 2 — CRM Sync */}
          <article className="feature reveal" data-d="2">
            <div className="fnum">02</div>
            <div className="kills">{t('feat.c2.kills')}</div>
            <h3 className="h-card">{t('feat.c2.title')}</h3>
            <p className="body">{t('feat.c2.body')}</p>
            <div className="demo mock">
              <div className="fcard sync">
                <div className="fc-pad">
                  <div className="fc-title">
                    Sync Deal Status <span className="badge2">HUBSPOT</span>
                  </div>
                  <div className="fc-sub">
                    <CiteIcon />
                    Re: Mindstream AI for Granola sales team
                  </div>
                  <div className="fc-block">
                    <div className="fc-lbl">Matched records</div>
                    <div className="fc-kv">
                      <span className="k">Company</span>
                      <span className="v">Granola</span>
                      <span className="k">Deal</span>
                      <span className="v">Mindstream AI — Sales Team</span>
                      <span className="k">Contact</span>
                      <span className="v">Chris Pedregal</span>
                    </div>
                  </div>
                  <div className="fc-block">
                    <div className="fc-lbl">Stage update</div>
                    <div className="fc-stage">
                      <span className="old">Qualification</span>
                      <span className="ar2">→</span>
                      <span className="new">Presentation Scheduled</span>
                    </div>
                    <div className="fc-note">Budget confirmed and demo meeting discussed in the thread.</div>
                  </div>
                  <div className="fc-block">
                    <div className="fc-lbl">Tasks</div>
                    <div className="fc-task">
                      <span className="box" />
                      Send product demo deck to Chris<span className="due">Mar 25</span>
                    </div>
                    <div className="fc-task">
                      <span className="box" />
                      Schedule onboarding kickoff call<span className="due">Mar 30</span>
                    </div>
                  </div>
                </div>
                <div className="fc-actions">
                  <button className="sync-btn">
                    <svg viewBox="0 0 16 16" fill="none">
                      <path
                        d="M13 3v3h-3M3 13v-3h3M13 6a5 5 0 0 0-9-2M3 10a5 5 0 0 0 9 2"
                        stroke="currentColor"
                        strokeWidth="1.4"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                    Sync to HubSpot
                  </button>
                  <span className="dismiss">Dismiss</span>
                </div>
              </div>
            </div>
            <div className="feat-foot">
              <span className="chip">
                <span className="dot" />
                {t('feat.c2.foot1')}
              </span>
              <span className="chip">{t('feat.c2.foot2')}</span>
            </div>
          </article>

          {/* Card 3 — Proactive Nudges */}
          <article className="feature reveal" data-d="3">
            <div className="fnum">03</div>
            <div className="kills">{t('feat.c3.kills')}</div>
            <h3 className="h-card">{t('feat.c3.title')}</h3>
            <p className="body">{t('feat.c3.body')}</p>
            <div className="demo mock">
              <div className="fcard outreach">
                <div className="fc-pad">
                  <div className="fc-title">
                    Strategic outreach <span className="badge2 amber">SUGGESTED</span>
                  </div>
                  <div className="fc-sub">
                    <CiteIcon />
                    Quarterly Planning Cycles
                  </div>
                </div>
                <div className="oc-panel">
                  <div className="oc-kv">
                    <span className="k">Lead</span>
                    <span className="v">Rippling</span>
                    <span className="k">Contact</span>
                    <span className="v">
                      Michael Chen<span className="role">Sr Manager, Procurement</span>
                    </span>
                  </div>
                  <div className="oc-div" />
                  <div className="oc-lbl">Suggested</div>
                  <div className="oc-text">
                    Ask if they want a revised proposal with a lighter implementation scope to fit budget.
                  </div>
                  <div className="oc-div" />
                  <div className="oc-lbl">Why now</div>
                  <div className="oc-text quote">
                    "Last thread ended with timing/budget constraints — a smaller scope often unlocks
                    re-engagement."
                  </div>
                </div>
                <div className="oc-foot">
                  <span className="vdr">View draft reply</span>
                  <span className="acts">
                    <span className="complete">Complete</span>
                    <span>Dismiss</span>
                  </span>
                </div>
              </div>
            </div>
            <div className="feat-foot">
              <span className="chip">
                <span className="dot" />
                {t('feat.c3.foot1')}
              </span>
              <span className="chip">{t('feat.c3.foot2')}</span>
            </div>
          </article>
        </div>
      </div>
    </section>
  );
}
